import { Flex, Table, Modal, Button, Status, Tooltip } from "@components/index";
import { useState } from "react";
import { useToast } from "@celebration/react";
import { useOrder } from "@pages/Processes/List/hooks/useOrder";
import { useProcess } from "@services/process/hooks";
import { useSafeNavigate } from "@routes/hooks/useSafeNavigate";
import { formatTimeUtcToTimeLocal } from "@utils/datetime";
import { useTranslation } from "react-i18next";
import withShow from "@common/withShow";

import style from "../ProcessesList.module.scss";

const Content: React.FC<{
  data?: Sentinel.Processes.GetAllProcessesResponse[];
  reload: () => void;
}> = ({ data = [], reload }) => {
  const toast = useToast();
  const navigate = useSafeNavigate();
  const { t } = useTranslation();

  const { deleteProcess } = useProcess();
  const { order, handleColumnOrder, getColumnOrder } = useOrder(data);

  const [modal, setModal] =
    useState<Sentinel.Processes.GetAllProcessesResponse>();

  const handleEditProcesses = (id: number) =>
    void navigate(`/processes/edit/${id}`);

  const handleDeleteProcesses = (
    processes: Sentinel.Processes.GetAllProcessesResponse,
  ) => setModal(processes);

  const actionModalCancel = () => setModal(undefined);

  const actionModalConfirm = async () => {
    try {
      await deleteProcess(modal.id);

      reload();
      setModal(undefined);

      toast.openToast({
        title: "Sucesso",
        description: "Processo deletado com sucesso!",
        type: "positive",
        autoClose: true,
      });
    } catch (_) {
      toast.openToast({
        title: "Erro",
        description: "Ocorreu um erro ao realizar a operação",
        type: "positive",
        autoClose: true,
      });
    }
  };

  return (
    <div className={style.content}>
      <Table
        data={order}
        columns={[
          {
            title: "Nome Exibição",
            dataIndex: "displayName",
            showColumnOrderIcon: true,
            columnOrder: getColumnOrder("displayName"),
            onColumnOrder: (newOrder) =>
              handleColumnOrder("displayName", newOrder),
          },
          {
            title: "Tipo",
            cellRender({ isOneShot }) {
              if (isOneShot) {
                return (
                  <Tooltip
                    title={t("process.oneshot.badge")}
                    description={t("process.oneshot.tooltip.description")}
                    position="top"
                  >
                    <Status variant="info">{t("process.oneshot.badge")}</Status>
                  </Tooltip>
                ) as React.ReactNode;
              }
              return (
                <Status variant="neutral">{t("process.standard.badge")}</Status>
              ) as React.ReactNode;
            },
          },
          {
            title: "Crítico",
            cellRender({ critical }) {
              return (
                <span
                  className={
                    critical ? "text--danger--500" : "text--neutral--500"
                  }
                >
                  {critical ? "Sim" : "Não"}
                </span>
              ) as React.ReactNode;
            },
          },
          {
            title: "Tolerância",
            cellRender({ tolerancePeriod }) {
              return tolerancePeriod;
            },
          },
          {
            title: "Inicio Planejado",
            cellRender({ plannedExecutionStart }) {
              return formatTimeUtcToTimeLocal(
                plannedExecutionStart,
                "-",
              ) as React.ReactNode;
            },
          },
          {
            title: "Fim Planejado",
            cellRender({ plannedExecutionEnd }) {
              return formatTimeUtcToTimeLocal(
                plannedExecutionEnd,
                "-",
              ) as React.ReactNode;
            },
          },
          {
            title: "Ações",
            headerAlign: "center",
            cellRender: (
              element: Sentinel.Processes.GetAllProcessesResponse,
            ) => (
              <Flex justifyContent="end">
                <Button
                  icon="edit"
                  label="Editar"
                  buttonStyle="tertiary"
                  onClick={() => handleEditProcesses(element.id)}
                />

                <Button
                  icon="delete"
                  label="Deletar"
                  buttonStyle="tertiary"
                  onClick={() => handleDeleteProcesses(element)}
                />
              </Flex>
            ),
          },
        ]}
      />

      <Modal.Confirmation
        title="Confirmação"
        description="Você deseja realmente excluir o processo?"
        onCancel={actionModalCancel}
        onConfirm={() => void actionModalConfirm()}
        open={!!modal}
      />
    </div>
  );
};

export default withShow(Content);
